if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this._handleSizeSystemChange = this._handleSizeSystemChange.bind(this);
        this._handleCartUpdated = this._handleCartUpdated.bind(this);
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('.atc-text');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Mobile peek bar's submit button lives outside <product-form> (linked via
        // the form="..." attribute instead) so it never gets picked up by the
        // querySelector above — find it separately and mirror every state change
        // onto it below.
        this.submitButtonPeek = document.getElementById(`ProductSubmitButtonPeek-${this.dataset.sectionId}`);

        // Hover: show "Select Size" text only when no variant selected
        // (hover-capable devices only — touch taps fire mouseenter and would
        // swap the text away from "Add to cart")
        this.submitButton.addEventListener('mouseenter', () => {
          if (!window.matchMedia('(hover: hover)').matches) return;
          if (this.submitButton.classList.contains('no-size__selected')) {
            this.submitButton.classList.add('atc--hover-select-size');
          }
        });
        this.submitButton.addEventListener('mouseleave', () => {
          // Same guard as mouseenter — without it, mobile browsers' synthetic
          // mouseleave (fired after a touch tap ends) strips the class the
          // click handler just added, flashing back to "Add to Cart" text
          // before the sold-out/Notify Me determination even comes back.
          if (!window.matchMedia('(hover: hover)').matches) return;
          this.submitButton.classList.remove('atc--hover-select-size');
        });

        this.submitButtonPeek?.addEventListener('mouseenter', () => {
          if (!window.matchMedia('(hover: hover)').matches) return;
          if (this.submitButtonPeek.classList.contains('no-size__selected')) {
            this.submitButtonPeek.classList.add('atc--hover-select-size');
          }
        });
        this.submitButtonPeek?.addEventListener('mouseleave', () => {
          if (!window.matchMedia('(hover: hover)').matches) return;
          this.submitButtonPeek.classList.remove('atc--hover-select-size');
        });
      }

      connectedCallback() {
        document.addEventListener('product:size-system-change', this._handleSizeSystemChange);
        document.addEventListener('cart:updated', this._handleCartUpdated);
        this._pageLoadComplete = true;
        this._initMobilePdpAddedBarCartListener();
      }

      disconnectedCallback() {
        document.removeEventListener('product:size-system-change', this._handleSizeSystemChange);
        document.removeEventListener('cart:updated', this._handleCartUpdated);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // Variant change still rendering — form inputs hold the previous variant
        if (this.variantSyncPending) return;

        // No size selected — on mobile collapsed state, open size-peek instead
        if (this.submitButton.classList.contains('no-size__selected')) {
          const isCollapsed = this._isPdpPanelCollapsed();
          const isMobile = window.innerWidth <= 1023;

          if (isMobile && isCollapsed && typeof window.sizePeekOpen === 'function') {
            const sizePopupEl = document.querySelector('.size-popup-wrapper');
            const isSizePopupOpen = sizePopupEl && sizePopupEl.classList.contains('is-open');

            if (!isSizePopupOpen) {
              window.sizePeekOpen();
              this.submitButton.classList.add('atc--hover-select-size');
              this.submitButtonPeek?.classList.add('atc--hover-select-size');
              return;
            }

            // Popup already open — show warning inside the fieldset so it's visible to the user
            const fieldset = sizePopupEl.querySelector('.product-form__input--pill');
            const target = fieldset || sizePopupEl;
            let popupWarning = target.querySelector('.size-popup-warning');
            if (!popupWarning) {
              const sectionId = this.dataset.sectionId;
              const pageWarning = document.getElementById(`SelectSizeWarning-${sectionId}`);
              popupWarning = document.createElement('p');
              popupWarning.className = 'product-form__select-size-warning body-text size-popup-warning';
              popupWarning.setAttribute('role', 'alert');
              popupWarning.textContent = pageWarning ? pageWarning.textContent.trim() : 'Please select a size';
              target.appendChild(popupWarning);
            }
            popupWarning.hidden = false;
            this.submitButton.classList.add('atc--hover-select-size');
            this.submitButtonPeek?.classList.add('atc--hover-select-size');
            return;
          }

          const productInfo = this.closest('product-info');
          const sizeFieldset = productInfo?.querySelector('.product-form__input--pill')
            || document.querySelector('.product-form__input--pill');

          if (sizeFieldset) {
            // sizeFieldset.classList.remove('animate-size');
            void sizeFieldset.offsetWidth;
            // sizeFieldset.classList.add('animate-size');
            sizeFieldset.addEventListener('animationend', () => {
              // sizeFieldset.classList.remove('animate-size');
            }, { once: true });
          }

          // Show warning message
          const sectionId = this.dataset.sectionId;
          const warning = document.getElementById(`SelectSizeWarning-${sectionId}`);
          if (warning) {
            warning.hidden = false;
          }

          // Swap button text to "Select size" (mirrors hover behavior for touch/click)
          this.submitButton.classList.add('atc--hover-select-size');
          this.submitButtonPeek?.classList.add('atc--hover-select-size');

          return;
        }

        this.handleErrorMessage();
        this._setButtonState('adding');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // If size option uses multi-system format (e.g. "UK 38 / EU 39 / US 7"),
        // inject the currently-visible size value as a line item property.
        // Size radios are outside <form> (linked via form attr), so search closest product-info or document.
        // On mobile, the fieldset may be inside the size-peek popup (outside product-info) during/after selection.
        const productInfoEl = this.closest('product-info') || document;
        const sizeInput =
          productInfoEl.querySelector('.product-form__input--pill input[type="radio"]:checked') ||
          document.querySelector('.size-popup-wrapper .product-form__input--pill input[type="radio"]:checked');
        if (sizeInput && sizeInput.value && sizeInput.value.includes(' / ')) {
          const activeSystem = this._getActiveSizeSystem(productInfoEl);
          const parts = sizeInput.value.split(' / ');
          let displaySize = null;
          for (const part of parts) {
            const trimmed = part.trim();
            const subParts = trimmed.split(' ');
            if (subParts.length > 1 && subParts[0].toLowerCase() === activeSystem) {
              displaySize = subParts[1];
              break;
            }
          }
          if (displaySize) {
            formData.append('properties[Size]', `${displaySize} | ${activeSystem.toUpperCase()}`);
          }
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response, this._shouldUseMobilePdpAddedBar());
              });

              // Close size-peek popup on successful add to cart (mobile)
              const sizePopupEl = document.querySelector('.size-popup-wrapper');
              if (sizePopupEl && sizePopupEl.classList.contains('is-open') && typeof window.sizePeekClose === 'function') {
                window.sizePeekClose();
              }
              // Close size fieldset (desktop) on successful add to cart
              const sizeFieldset = document.querySelector('.product-form__input.product-form__input--pill.size-tabs-enabled');
              if (sizeFieldset) {
                sizeFieldset.classList.add('size-tabs-closed');
                const labelWrapper = sizeFieldset.querySelector('.form__label-wrapper');
                if (labelWrapper) {
                  labelWrapper.addEventListener('click', () => {
                    sizeFieldset.classList.remove('size-tabs-closed');
                  }, { once: true });
                }
              }

              if (this._shouldUseMobilePdpAddedBar()) {
                this._setButtonState('added');
                this._showMobilePdpAddedBar(
                  null,
                  this._getCartItemRawSizeValue(response)
                );
                window.clearTimeout(this._mobileAddedBarTimer);
                this._mobileAddedBarTimer = window.setTimeout(() => {
                  this._hideMobilePdpAddedBar();
                }, 5000);
                // Require size reselection before next add, but only if a size option exists
                if (this._hasSizePicker()) this._resetSizeSelectionForNextAdd();
              } else {
                console.log('Mobile PDP bar condition failed:', {
                  hasProductInfo: !!this.closest('product-info'),
                  hasProductParallax: !!this.closest('.product-paralax'),
                  isMobile: window.matchMedia('(max-width: 1023px)').matches
                });
              }

              this._pendingAdded = false;
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this._shouldUseMobilePdpAddedBar()) {
              this._setButtonState('default');
            }
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButtonPeek?.removeAttribute('aria-disabled');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      _setButtonState(state) {
        // Mirrored onto both the real submit button and its mobile peek-bar
        // twin (a separate element outside <product-form>, linked only via
        // form="...") so the adding/added text swap shows on whichever one
        // is actually visible.
        [this.submitButton, this.submitButtonPeek].filter(Boolean).forEach((btn) => {
          const textEl   = btn.querySelector('.atc-text');
          const addingEl = btn.querySelector('.atc-text--adding');
          const addedEl  = btn.querySelector('.atc-text--added');

          btn.classList.remove('atc--adding', 'atc--added');

          switch (state) {
            case 'adding':
              btn.setAttribute('aria-disabled', true);
              btn.classList.add('atc--adding');
              if (textEl)   textEl.hidden   = true;
              if (addingEl) addingEl.hidden = false;
              if (addedEl)  addedEl.hidden  = true;
              break;

            case 'added':
              btn.removeAttribute('aria-disabled');
              btn.classList.add('atc--added');
              if (textEl)   textEl.hidden   = false;
              if (addingEl) addingEl.hidden = true;
              if (addedEl)  addedEl.hidden  = true;
              break;

            default: // 'default'
              btn.removeAttribute('aria-disabled');
              if (textEl)   textEl.hidden   = false;
              if (addingEl) addingEl.hidden = true;
              if (addedEl)  addedEl.hidden  = true;
              break;
          }
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      _shouldUseMobilePdpAddedBar() {
        return Boolean(
          this.closest('product-info') &&
          this.closest('.product-paralax') &&
          window.matchMedia('(max-width: 1023px)').matches &&
          this._isPdpPanelCollapsed()
        );
      }

      // Collapsed = sticky top bar (peek row); once scrolled past the 50% reveal
      // mark the panel is expanded inline, and add-to-cart should behave like a
      // normal PDP (cart drawer opens) rather than showing the top-bar confirmation.
      _isPdpPanelCollapsed() {
        const panelReveal = typeof window.ScrollTrigger !== 'undefined'
          ? window.ScrollTrigger.getById('pdp-panel-reveal')
          : null;
        return panelReveal ? panelReveal.progress < 0.5 : true;
      }

      _showMobilePdpAddedBar(sizeLabelOverride = null, rawSizeOverride = null) {
        let bar = document.querySelector('.pdp-mobile-added-bar');
        const rawSizeValue = rawSizeOverride || this._getSelectedRawSizeValue() || '';
        const sizeLabel = this._escapeHtml(
          sizeLabelOverride || this._formatSizeLabel(rawSizeValue)
        );
        const checkoutUrl = this._escapeHtml(this._getCheckoutUrl());
        const addedText = this._escapeHtml(window.variantStrings?.addedToCart || 'Added to Cart');

        if (!bar) {
          bar = document.createElement('div');
          bar.className = 'pdp-mobile-added-bar';
          bar.setAttribute('role', 'status');
          bar.setAttribute('aria-live', 'polite');
        }

        this._mountMobilePdpAddedBar(bar);
        bar.dataset.rawSizeValue = rawSizeValue;

        bar.innerHTML = `
          <div class="pdp-mobile-added-bar__inner">
            <span class="pdp-mobile-added-bar__size heading-extra-small">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="14" viewBox="0 0 13 14" fill="none">
                <path d="M10.5157 4.41051C10.5157 2.11273 8.70469 0.25 6.47072 0.25C4.23676 0.25 2.42578 2.11273 2.42578 4.41051" stroke="white" stroke-width="0.5"/>
                <path d="M1.61816 4.19922H11.3262C12.0815 4.1994 12.6934 4.81198 12.6934 5.56738V11.5771C12.6932 12.3324 12.0814 12.9442 11.3262 12.9443H1.61816C0.862758 12.9443 0.250173 12.3325 0.25 11.5771V5.56738C0.25 4.81187 0.862651 4.19922 1.61816 4.19922Z" stroke="white" stroke-width="0.5"/>
              </svg>
              ${sizeLabel}
            </span>
            <span class="pdp-mobile-added-bar__message body-text">${addedText}</span>
            <a class="pdp-mobile-added-bar__checkout body-text" href="${checkoutUrl}">Check Out</a>
          </div>
        `;

        bar.classList.remove('is-visible');
        void bar.offsetWidth;
        bar.classList.add('is-visible');
      }

      _hasSizePicker() {
        // Check both locations — the fieldset may still be parked in .size-popup-wrapper
        const productInfoEl = this.closest('product-info') || document;
        const hasSizeRadios = !!(
          productInfoEl.querySelector('.product-form__input--pill input[type="radio"]') ||
          document.querySelector('.size-popup-wrapper .product-form__input--pill input[type="radio"]')
        );
        const isSingleSize = !!(
          productInfoEl.querySelector('.product-form__input--pill[data-single-size]') ||
          document.querySelector('.size-popup-wrapper .product-form__input--pill[data-single-size]')
        );
        return hasSizeRadios && !isSingleSize;
      }

      _resetSizeSelectionForNextAdd() {
        const submitBtn = this.submitButton;
        submitBtn.classList.add('no-size__selected');
        this.submitButtonPeek?.classList.add('no-size__selected');

        if (this.variantIdInput) this.variantIdInput.value = '';

        const productInfoEl = this.closest('product-info') || document;
        const sizeInputs = productInfoEl.querySelectorAll('.product-form__input--pill input[type="radio"]');
        sizeInputs.forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });

        const popupSizeInputs = document.querySelectorAll('.size-popup-wrapper .product-form__input--pill input[type="radio"]');
        popupSizeInputs.forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });

        const bisBtn = this.querySelector(`#BISTrigger-${this.dataset.sectionId}`) || document.querySelector('.pdp-bis__trigger');
        if (bisBtn) bisBtn.classList.add('no-size__selected');

        const bisBtnPeek = document.getElementById(`BISTriggerPeek-${this.dataset.sectionId}`);
        if (bisBtnPeek) bisBtnPeek.classList.add('no-size__selected');
      }

      _hideMobilePdpAddedBar() {
        this._setButtonState('default');

        const bar = document.querySelector('.pdp-mobile-added-bar');
        if (!bar) return;

        bar.classList.remove('is-visible');
        delete bar.dataset.rawSizeValue;

        const productInfoEl = this.closest('product-info') || document;
        const sizeInputs = productInfoEl.querySelectorAll('.product-form__input--pill input[type="radio"]');
        sizeInputs.forEach(input => input.checked = false);

        const sizePopupInputs = document.querySelectorAll('.size-popup-wrapper .product-form__input--pill input[type="radio"]');
        sizePopupInputs.forEach(input => input.checked = false);
      }

      _syncMobilePdpAddedBarWithCart(cart = null) {
        if (!this._shouldUseMobilePdpAddedBar()) return;

        const productInfoEl = this.closest('product-info');
        const productId = Number(productInfoEl?.dataset.productId);
        if (!productId) return;

        const syncToken = Date.now();
        this._mobileAddedBarSyncToken = syncToken;

        const syncBar = (resolvedCart) => {
          if (this._mobileAddedBarSyncToken !== syncToken) return;

          const currentProductItem = resolvedCart?.items?.find((item) => Number(item.product_id) === productId);

          if (!currentProductItem) {
            this._hideMobilePdpAddedBar();
            return;
          }

          this._showMobilePdpAddedBar(
            null,
            this._getCartItemRawSizeValue(currentProductItem)
          );
          window.clearTimeout(this._mobileAddedBarTimer);
          this._mobileAddedBarTimer = window.setTimeout(() => {
            this._hideMobilePdpAddedBar();
          }, 5000);
        };

        const cartPromise = Array.isArray(cart?.items)
          ? Promise.resolve(cart)
          : fetch(this._getCartJsonUrl(), {
              credentials: 'same-origin',
              headers: {
                Accept: 'application/json',
              },
            }).then((response) => {
              if (!response.ok) throw new Error('Unable to load cart');
              return response.json();
            });

        cartPromise
          .then((cart) => {
            syncBar(cart);
          })
          .catch(() => {
            if (this._mobileAddedBarSyncToken !== syncToken) return;
            this._hideMobilePdpAddedBar();
          });
      }

      _handleCartUpdated(event) {
        const cartData = event?.detail?.cartData;
        if (!this._shouldUseMobilePdpAddedBar()) return;
        if (!cartData) return;

        if (cartData.item_count === 0) {
          this._mobileAddedBarSyncToken = Number.MAX_SAFE_INTEGER;
          this._hideMobilePdpAddedBar();
          return;
        }

        if (Array.isArray(cartData.items)) {
          this._syncMobilePdpAddedBarWithCart(cartData);
          return;
        }

        this._syncMobilePdpAddedBarWithCart();
      }

      _initMobilePdpAddedBarCartListener() {
        if (this._cartListenerBound) return;
        if (!this._shouldUseMobilePdpAddedBar()) return;

        this._cartListenerBound = true;
        subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
          if (!this._shouldUseMobilePdpAddedBar()) return;
          this._syncMobilePdpAddedBarWithCart(cartData);
        });
      }

      _mountMobilePdpAddedBar(_bar) {
        // Bar is statically rendered in main-product.liquid — no DOM move needed
      }

      _handleSizeSystemChange() {
        const bar = document.querySelector('.pdp-mobile-added-bar');
        if (!bar || !bar.classList.contains('is-visible')) return;

        const sizeEl = bar.querySelector('.pdp-mobile-added-bar__size');
        if (!sizeEl) return;

        const rawSizeValue = this._getSelectedRawSizeValue() || bar.dataset.rawSizeValue || '';
        const sizeLabel = this._escapeHtml(this._formatSizeLabel(rawSizeValue));
        if (!sizeLabel) return;

        bar.dataset.rawSizeValue = rawSizeValue;
        const svgMarkup = sizeEl.querySelector('svg')?.outerHTML || '';
        sizeEl.innerHTML = `${svgMarkup}${sizeLabel}`;
      }

      _getSelectedRawSizeValue() {
        const productInfoEl = this.closest('product-info') || document;
        const hasSizePills = !!productInfoEl.querySelector('.product-form__input--pill input[type="radio"]');
        if (!hasSizePills) return '';

        const sizeInput =
          productInfoEl.querySelector('.product-form__input--pill input[type="radio"]:checked') ||
          document.querySelector('.size-popup-wrapper .product-form__input--pill input[type="radio"]:checked');

        return sizeInput?.dataset.optionValue || sizeInput?.value || '';
      }

      _getSelectedSizeLabel() {
        return this._formatSizeLabel(this._getSelectedRawSizeValue());
      }

      _getCartItemRawSizeValue(item) {
        const sizeOption = item.options_with_values?.find((option) => {
          const name = option.name?.trim().toLowerCase();
          return name === 'size' || name === 'shoe size';
        });

        if (sizeOption?.value) return sizeOption.value;

        if (item.properties?.Size) {
          const parts = String(item.properties.Size).split('|');
          if (parts.length === 2) {
            return `${parts[1].trim()} ${parts[0].trim()}`;
          }
        }

        return '';
      }

      _getCartItemSizeLabel(item) {
        return this._formatSizeLabel(this._getCartItemRawSizeValue(item));
      }

      _formatSizeLabel(size) {
        if (!size) return '';

        if (size.includes(' / ')) {
          const activeSystem = this._getActiveSizeSystem();
          const matchingPart = size.split(' / ').find((part) => {
            return part.trim().split(' ')[0]?.toLowerCase() === activeSystem;
          });
          size = matchingPart ? matchingPart.trim().split(' ').slice(1).join(' ') : size.split(' / ')[0].trim();
        }

        size = size.replace(/^size\s*/i, '').trim();
        return size ? `SIZE ${size}` : '';
      }

      _getCheckoutUrl() {
        const cartUrl = window.routes?.cart_url || '/cart';
        return `${cartUrl.replace(/\/$/, '')}/checkout`;
      }

      _getCartJsonUrl() {
        const cartUrl = window.routes?.cart_url || '/cart';
        return `${cartUrl.replace(/\/$/, '')}.js`;
      }

      _getActiveSizeSystem(scope = null) {
        const root = scope instanceof Element ? scope : this.closest('product-info') || document;
        const activeTab =
          root.querySelector('.size-tab.active') ||
          document.querySelector('.size-popup-wrapper .size-tab.active') ||
          document.querySelector('.size-tab.active');

        return activeTab?.dataset.sizeSystem || sessionStorage.getItem('preferred-size-system') || 'eu';
      }

      _escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (character) => {
          return {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
          }[character];
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
